import React, { Dispatch, SetStateAction } from 'react';
import { SalvProgressCircle, SalvTable, SalvTablePaginationType } from '@salvapp/ui-components';
import { InternalApiSortDirection, InternalApiTransaction, InternalApiTransactionsPage } from '../api-internal';
import { PaginationState, Row, SortingState, Updater } from '@tanstack/react-table';
import { TransactionSearchQuery } from './TransactionSearch';
import { SearchParamKeys } from '../common/searchParamKeys';
import DataLoadingErrorFallback from '../common/errors/DataLoadingErrorFallback';
import './TransactionSearchResults.scss';
import { getOrderByAndDirection } from '../common/sort';
import useTransactionColumns from '../transaction/table/useTransactionColumns';
import { getTransactionPath } from '../navigation/helper';
import { useNavigate, SetURLSearchParams } from 'react-router';

interface TransactionSearchResultsProps {
  transactionsPage: InternalApiTransactionsPage;
  isTransactionSearchFetching: boolean;
  submittedSearchState: TransactionSearchQuery;
  setSubmittedSearch: Dispatch<SetStateAction<TransactionSearchQuery>>;
  setSearchParams: SetURLSearchParams;
  selectedRows?: InternalApiTransaction[];
  setSelectedRows?: Dispatch<SetStateAction<InternalApiTransaction[]>>;
  isRowSelectionEnabled?: (row: Row<InternalApiTransaction>) => boolean;
}

const TransactionSearchResults = ({
  transactionsPage,
  isTransactionSearchFetching,
  submittedSearchState,
  setSubmittedSearch,
  setSearchParams,
  selectedRows,
  setSelectedRows,
  isRowSelectionEnabled,
}: TransactionSearchResultsProps) => {
  const navigate = useNavigate();
  const {
    columns,
    isPending: isColumnsPending,
    isError: isColumnsError,
  } = useTransactionColumns({
    transactions: transactionsPage.content,
    enableSorting: true,
  });

  if (isColumnsError) {
    return <DataLoadingErrorFallback message={'Could not load Transaction Search Results'} />;
  } else if (isColumnsPending) {
    return <SalvProgressCircle ariaLabel={'Loading Transaction Search Results'} />;
  }

  const setPagination = (state: PaginationState) => {
    setSubmittedSearch((prev) => {
      return {
        ...prev,
        page: state.pageIndex,
      };
    });
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      newParams.set(SearchParamKeys.page, state.pageIndex.toString());

      return newParams;
    });
  };

  const sortingState: SortingState = [
    {
      id: submittedSearchState.orderBy || '',
      desc: submittedSearchState.direction === InternalApiSortDirection.DESC,
    },
  ];

  const setSorting = (updaterOrValue: Updater<SortingState>) => {
    const { orderBy, direction } = getOrderByAndDirection(updaterOrValue, sortingState);

    setSubmittedSearch((prev) => {
      return {
        ...prev,
        orderBy,
        direction,
      };
    });
    setSearchParams((prev) => {
      const newParams = new URLSearchParams(prev);
      if (orderBy && direction) {
        newParams.set(SearchParamKeys.orderBy, orderBy);
        newParams.set(SearchParamKeys.direction, direction);
      }
      return newParams;
    });
  };

  return (
    <SalvTable
      isLoading={isTransactionSearchFetching}
      data={transactionsPage.content}
      columns={columns}
      pagination={{
        type: SalvTablePaginationType.PAGINATION,
        total: transactionsPage.total,
        state: {
          pageIndex: transactionsPage.number,
          pageSize: transactionsPage.size,
        },
        setPagination,
      }}
      sorting={{
        state: sortingState,
        setSorting: setSorting,
      }}
      selectedRows={selectedRows}
      setSelectedRows={setSelectedRows}
      isRowSelectionEnabled={isRowSelectionEnabled}
      emptyMessage='No transactions found'
      className={'transaction-search-results m-b-64'}
      rowClick={(row) => navigate(getTransactionPath(row.original.id))}
    />
  );
};

export default TransactionSearchResults;
